import { motion } from "framer-motion";
import { CalendarDays, Copy, CreditCard, Tickets } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "@/hooks/use-toast";

interface TripCardHeaderProps {
  destinationImage: string;
  destinationName: string;
  remainingDays: number;
  isFocused: boolean;
  onShare?: (method: 'native' | 'whatsapp' | 'email' | 'sms') => void;
  bookingReference?: string;
}

const TripCardHeader = ({
  destinationImage,
  destinationName,
  remainingDays,
  isFocused,
  bookingReference
}: TripCardHeaderProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // Format the days text based on whether the trip is in the past, today, or future
  const getDaysText = () => {
    if (remainingDays === 0) {
      return t("tripCard.today", "On-going");
    } else if (remainingDays < 0) {
      // For past trips (negative remainingDays)
      const daysAgo = Math.abs(remainingDays);
      return daysAgo === 1 
        ? t("tripCard.dayAgo", "1 day ago") 
        : t("tripCard.daysAgo", "{{count}} days ago", { count: daysAgo });
    } else {
      // For future trips (positive remainingDays)
      return remainingDays === 1 
        ? t("tripCard.remainingDay", "Today")
        : t("tripCard.remainingDays", "{{count}} remaining days", { count: remainingDays });
    }
  };

  // Default fallback image - UPDATED to use the new image
  const defaultImage = "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png";
  
  // Enhanced logging for image url resolution
  console.debug("🖼️ TripCardHeader image DEBUG:", {
    phase: "ENTRY POINT",
    platform: isMobile ? 'Mobile' : 'Desktop',
    destinationName,
    destinationImage,
    imageType: typeof destinationImage,
    imageLength: destinationImage?.length || 0,
    isNullOrUndefined: destinationImage == null,
    isEmptyString: destinationImage === "",
    destinationNameIdentified: !!destinationName && destinationName !== "Unknown" && destinationName !== "Unknown City",
    isValidDestinationIdentified: destinationName !== "Unknown" && destinationName !== "Unknown Destination" && destinationName !== "Unknown City",
    timestamp: new Date().toISOString(),
    bookingReference
  });

  // Check if the image URL exactly matches the default image
  const isExactlyDefaultImage = destinationImage === defaultImage;

  // Ensure we always have a valid destination image
  // Only use default if the provided image is null, undefined, empty string, or exactly matches the default image
  // This ensures we don't interpret the default image as a valid destination image
  const imageUrl = (destinationImage && 
                   destinationImage.trim().length > 0 && 
                   !isExactlyDefaultImage) 
    ? destinationImage 
    : defaultImage;

  console.debug("🖼️ TripCardHeader image RESOLUTION:", {
    phase: "AFTER PROCESSING",
    platform: isMobile ? 'Mobile' : 'Desktop',
    destinationName,
    providedImageUrl: destinationImage,
    finalImageUrl: imageUrl,
    didFallbackToDefault: isExactlyDefaultImage || !destinationImage || destinationImage.trim().length === 0,
    isMobile,
    timestamp: new Date().toISOString(),
    bookingReference
  });

  return (
    <div className={`relative ${isMobile ? 'h-36' : 'md:h-full md:min-h-[200px]'}`}>
      <motion.img 
        src={imageUrl} 
        alt={destinationName} 
        className="w-full h-full object-cover" 
        animate={isFocused ? {
          scale: [1, 1.03, 1],
          transition: { 
            duration: 8, 
            repeat: Infinity, 
            repeatType: "reverse", 
            ease: "easeInOut" 
          }
        } : {}}
        onLoad={(e) => {
          // Log when image successfully loads
          console.debug("🖼️ Image LOAD SUCCESS:", {
            phase: "IMAGE LOADED",
            platform: isMobile ? 'Mobile' : 'Desktop',
            destinationName,
            src: (e.target as HTMLImageElement).src,
            isDefault: (e.target as HTMLImageElement).src === defaultImage,
            timestamp: new Date().toISOString()
          });
        }}
        onError={(e) => {
          // If the image fails to load, set a default image
          const target = e.target as HTMLImageElement;
          console.debug("🖼️ Image LOAD ERROR:", {
            phase: "IMAGE ERROR",
            platform: isMobile ? 'Mobile' : 'Desktop',
            destinationName,
            originalSrc: target.src,
            error: e.type,
            timestamp: new Date().toISOString()
          });
          target.src = defaultImage;
          console.debug("🖼️ Image FALLBACK APPLIED:", {
            phase: "FALLBACK SET",
            platform: isMobile ? 'Mobile' : 'Desktop',
            destinationName,
            finalSrc: defaultImage,
            timestamp: new Date().toISOString()
          });
        }}
      />
      <div className="absolute top-3 left-3 bg-black/30 backdrop-blur-md rounded-full px-3 py-1 flex items-center gap-1">
        <CalendarDays className="w-3 h-3 text-white" />
        <span className="text-white text-xs font-medium">
          {getDaysText()}
        </span>
      </div>
       
      {bookingReference && (
        <div className="absolute bottom-3 left-3 bg-black/30 backdrop-blur-md rounded-full px-3 py-1">
          <span className="text-white text-xs font-medium flex items-center cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigator.clipboard.writeText(bookingReference);
                toast({
                  description: t("tripCard.bookingReferenceCopied", "Booking reference copied to clipboard"),
                  duration: 1000,
                });
              }}>
                <Tickets className="w-4 h-4 mr-1.5 " />
                {bookingReference}
          </span>
        </div>
      )}
    </div>
  );
};

export default TripCardHeader;
